import * as React from 'react'
import { AlertCircle as WarningCircleIcon, Cpu as CpuIcon } from 'lucide-react'
import {
  useGetPlantComputeIndexQuery,
  usePlantSessionPaint,
  type PlantComputeHostSummary
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import {
  COMPUTE_TYPE_KEYS,
  computeIconFor,
  computeSectionTitleFor,
  computeTypeKey,
  type ComputeTypeKey
} from './compute-icon'
import { PlantHref } from './PlantHref'
import {
  SessionGhostTile,
  SessionMarkAffordances,
  SessionRollupPill,
  collectUnplacedGhosts,
  sessionMarkClass,
  type PlantSessionGhostView
} from './session-mark-ui'

/**
 * `/plant/compute` — Compute tab landing.
 *
 * Renders every `Compute` host in the ontology, grouped by
 * `compute_type` so the user can scan all gateways, all database
 * servers, etc. as separate sections. Per-host badges only surface
 * counts that are *meaningful for that type* (no "0 providers" on a
 * DB server card). Counts in the Cypher come from `getPlantComputeIndex`
 * so the grid renders without N+1 follow-ups.
 *
 * Devices (Ignition PLC connections mapped to `compute_type: 'device'`)
 * get their own first-class section. Each device card surfaces the
 * driver kind (e.g. ModbusTcp) plus a `via {gateway}` chip so the
 * user can see which Ignition gateway the field hardware is wired to
 * without drilling into the host detail page.
 *
 * When a session is painting, host tiles carry marks + descendant
 * rollups, and deleted root hosts render in a trailing ghost section.
 */
const ComputePage = (): React.ReactElement => {
  const { data, isLoading, isError } = useGetPlantComputeIndexQuery()
  const hosts = data ?? []
  const groups = React.useMemo(() => groupHostsByType(hosts), [hosts])
  const { active, filter, markById, ghostsByContainerId, markFor } = usePlantSessionPaint()
  const paintOn = active && filter.enabled

  const hostGhosts = React.useMemo((): PlantSessionGhostView[] => {
    if (!paintOn) return []
    return collectUnplacedGhosts(markById, ghostsByContainerId, markFor, {
      label: 'Compute',
      liveIds: new Set(hosts.map((host) => host.id))
    })
  }, [ghostsByContainerId, hosts, markById, markFor, paintOn])

  const showEmpty = !isError && !isLoading && hosts.length === 0 && hostGhosts.length === 0

  return (
    <div className="flex flex-1 flex-col gap-card">
      <header>
        <h1 className="m-0 text-2xl font-semibold text-ink">Compute</h1>
        <p className="mt-1 text-sm text-ink-3">{computeSubtitle(hosts)}</p>
      </header>

      {isError ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="Couldn't load compute hosts"
            hint="Check the proxy is reachable and try again."
            icon={WarningCircleIcon}
          />
        </div>
      ) : isLoading && hosts.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <div className="text-sm text-ink-3">Loading compute hosts…</div>
        </div>
      ) : showEmpty ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="No compute hosts yet"
            hint="Ingest an Ignition, AVEVA, or OPC source, or a DB dump, to populate the Compute tab."
            icon={CpuIcon}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-card">
          {COMPUTE_TYPE_KEYS.map((typeKey) => {
            const groupHosts = groups[typeKey]
            if (groupHosts.length === 0) return null
            return (
              <section key={typeKey}>
                <SectionHeader
                  label={computeSectionTitleFor(typeKey)}
                  subtitle={`${groupHosts.length} ${groupHosts.length === 1 ? 'host' : 'hosts'}`}
                />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))]">
                  {groupHosts.map((host) => (
                    <HostTile key={host.id} host={host} typeKey={typeKey} />
                  ))}
                </div>
              </section>
            )
          })}
          {hostGhosts.length > 0 && (
            <section>
              <SectionHeader
                label="Deleted hosts"
                subtitle={`${hostGhosts.length} ${hostGhosts.length === 1 ? 'host' : 'hosts'}`}
              />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))]">
                {hostGhosts.map((ghost) => (
                  <SessionGhostTile
                    key={ghost.id}
                    name={ghost.name ?? ghost.id}
                    view={ghost.view}
                    icon={
                      <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-st-down-bg text-st-down">
                        <CpuIcon size={20} />
                      </span>
                    }
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}

function HostTile({
  host,
  typeKey
}: {
  host: PlantComputeHostSummary
  typeKey: ComputeTypeKey
}): React.ReactElement {
  const Icon = computeIconFor(host.compute_type)
  const subtitle = host.url ?? host.host
  const { markFor, rollupByContainerId, touchedRollupByContainerId, deletedRollupByContainerId } =
    usePlantSessionPaint()
  const mark = markFor(host.id)
  const added = rollupByContainerId.get(host.id) ?? 0
  const modified = touchedRollupByContainerId.get(host.id) ?? 0
  const deleted = deletedRollupByContainerId.get(host.id) ?? 0

  return (
    <PlantHref
      id={host.id}
      label="Compute"
      node={host}
      className={sessionMarkClass(
        'flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
        <Icon size={20} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block min-w-0 flex-1 truncate text-sm font-semibold text-ink">
            {host.name}
          </span>
          <SessionMarkAffordances mark={mark} />
        </span>
        {subtitle && (
          <span className="block truncate font-mono text-12 text-ink-3">{subtitle}</span>
        )}
        <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {hostBadges(typeKey, host)}
          <SessionRollupPill added={added} modified={modified} deleted={deleted} />
        </span>
      </span>
    </PlantHref>
  )
}

/**
 * Chips for the inbound `NETWORKS_WITH` neighbours of a Compute. On a
 * device, this is the Ignition gateway(s) the PLC is wired to; on an
 * OPC / DB server it's the gateway(s) that talk to it. Rendered as
 * outline `via {gateway-name}` badges so the user can tell at a
 * glance which gateway owns a given field host without drilling in.
 */
function gatewayBadges(host: PlantComputeHostSummary): React.ReactNode {
  if (host.gateways.length === 0) return null
  return host.gateways.map((gw) => (
    <Badge key={gw.id} variant="outline" title={`Networks with ${gw.name}`}>
      via {gw.name}
    </Badge>
  ))
}

/**
 * Chips for outbound `MAPS_TO_CONTROLLER` edges on opc/device hosts.
 * Rendered as `→ {controller-name}` badges so the user can tell which
 * PLC a field host addresses without drilling in.
 */
function controllerBadges(host: PlantComputeHostSummary): React.ReactNode {
  if (host.controllers.length === 0) return null
  return host.controllers.map((ctrl) => (
    <Badge key={ctrl.id} variant="outline" title={`Maps to ${ctrl.name}`}>
      → {ctrl.name}
    </Badge>
  ))
}

function dcsControllerBadges(host: PlantComputeHostSummary): React.ReactNode {
  if (host.dcsControllers.length === 0) return null
  return host.dcsControllers.map((ctrl) => (
    <Badge key={ctrl.id} variant="outline" title={`Maps to DCS ${ctrl.name}`}>
      → {ctrl.name}
    </Badge>
  ))
}

/**
 * Counts shown on a host card depend on the host's type — surfacing
 * a "0 providers" chip on a DB server (or "0 databases" on an OPC
 * server) makes the card look broken. Gateways own both, so we show
 * providers always (the headline metric) and databases only if
 * non-zero. Devices don't host providers or databases — they're
 * field hardware — so the device card uses its `device_driver`
 * (ModbusTcp / LogixDriver / ...) plus the inbound gateway badges
 * to fill the chip row instead. The gateway chip is appended to
 * every type that can carry an inbound `NETWORKS_WITH` edge so the
 * "which gateway is this part of?" question is answerable from the
 * Compute index without drilling in.
 */
function hostBadges(typeKey: ComputeTypeKey, host: PlantComputeHostSummary): React.ReactNode {
  const providers = (
    <Badge variant="outline">
      {host.tagProviderCount} {host.tagProviderCount === 1 ? 'provider' : 'providers'}
    </Badge>
  )
  const databases = (
    <Badge variant="outline">
      {host.databaseCount} {host.databaseCount === 1 ? 'database' : 'databases'}
    </Badge>
  )
  const gateways = gatewayBadges(host)
  const controllers = controllerBadges(host)
  const dcsControllers = dcsControllerBadges(host)

  switch (typeKey) {
    case 'ignition':
    case 'aveva':
      return (
        <>
          {providers}
          {host.databaseCount > 0 ? databases : null}
          {gateways}
        </>
      )
    case 'opc':
      return (
        <>
          {providers}
          {gateways}
          {controllers}
          {dcsControllers}
        </>
      )
    case 'dbserver':
      return (
        <>
          {databases}
          {gateways}
        </>
      )
    case 'device':
      return (
        <>
          {host.device_driver ? <Badge variant="outline">{host.device_driver}</Badge> : null}
          {gateways}
          {controllers}
          {dcsControllers}
        </>
      )
    case 'other':
    default:
      return (
        <>
          {host.tagProviderCount > 0 ? providers : null}
          {host.databaseCount > 0 ? databases : null}
          {gateways}
          {dcsControllers}
        </>
      )
  }
}

function groupHostsByType(
  hosts: PlantComputeHostSummary[]
): Record<ComputeTypeKey, PlantComputeHostSummary[]> {
  const groups: Record<ComputeTypeKey, PlantComputeHostSummary[]> = {
    ignition: [],
    aveva: [],
    opc: [],
    dbserver: [],
    device: [],
    other: []
  }
  for (const host of hosts) {
    groups[computeTypeKey(host.compute_type)].push(host)
  }
  return groups
}

function SectionHeader({
  label,
  subtitle
}: {
  label: string
  subtitle: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-baseline gap-3">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">{label}</h3>
      <span className="text-xs text-ink-3">{subtitle}</span>
    </div>
  )
}

function computeSubtitle(hosts: PlantComputeHostSummary[]): string {
  if (hosts.length === 0) {
    return 'Hosts, tag providers, and databases that back the ontology.'
  }
  let providerCount = 0
  let databaseCount = 0
  let deviceCount = 0
  for (const host of hosts) {
    providerCount += host.tagProviderCount
    databaseCount += host.databaseCount
    if (computeTypeKey(host.compute_type) === 'device') deviceCount += 1
  }
  const segments = [
    `${hosts.length} ${hosts.length === 1 ? 'host' : 'hosts'}`,
    `${providerCount} ${providerCount === 1 ? 'provider' : 'providers'}`,
    `${databaseCount} ${databaseCount === 1 ? 'database' : 'databases'}`
  ]
  if (deviceCount > 0) {
    segments.push(`${deviceCount} ${deviceCount === 1 ? 'device' : 'devices'}`)
  }
  return `${segments.join(' · ')}.`
}

export default ComputePage
